import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { assetPathFnState } from '../../recoil/atoms';
import { ProgressBar } from '../components/ProgressBar.component';
import { MeasurementSystem } from '../enums/measurement-system.enum';
import { Option } from '../models/option';
import { StepViewModel } from '../models/step';
import { calculateSizeFromStepValues } from '../utils/size-calculator';
import { GoogleAnalyticsService } from '../services/google-analytics.service';
import { HoneyLoveService } from '../services/honeylove/honeylove.service';
import { SizingToolResponse } from '../services/honeylove/response';
import { v4 as uuid } from 'uuid';
import { parseEngineVariables } from '../utils/engine-helper';
import { StepValues } from '../models/step-values';
import { Size } from '../enums/size.enum';
import { Step } from '../components/Step.component';
import { StepsMetadata } from '../constants/steps-metadata';
import { ViewProduct } from '../../models/ViewProduct.model';
import { SubContext } from '../../enums/SubContext.enum';
import { SelectionType } from '../../enums/SelectionType.enum';
import { FindYourSizeSelector } from '../../product_detail/components/FindYourSizeSelector';
import { modalState, sizingToolState } from '../../recoil/selectors';
import { ModalToggleState, SizingToolState } from '../../models/actionStates';
import styles from './FunnelView.module.scss';
import { TreeNode, getCurrentProgress } from '../utils/sizing-tool-progress-helper';
import { LongSize } from '../../product_detail/components/size.enum';
import { ProductHandle } from '../../enums/ProductHandle.enum';
import { DivButton } from '../../root/components/DivButton';
import { useProductSelection } from '../../hooks/useProductSelection';
import { Icon } from '../../icons/Icon';
import { CardHeader } from '../../components/CardHeader';
import { CardFooter } from '../../components/CardFooter';
import { CardBody } from '../../components/CardBody';
import { useSetModalState } from '../../hooks/actions/useModalState';
import { Fit } from '../../enums/Fit.enum';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const dataTree = require('data-tree');

interface Props {
  selectionType: SelectionType;
  selectionKey: string;
  subContext?: SubContext;
}

const funnelId = 181;

// eslint-disable-next-line max-lines-per-function
export const FunnelView: React.FC<Props> = ({ selectionType, selectionKey, subContext }) => {
  const assetPathFn = useRecoilValue(assetPathFnState);
  const [activeMeasurement, toggleMeasurement] = useState<MeasurementSystem>(MeasurementSystem.Imperial);
  const [stepValues, setStepValues] = useState<StepValues>(new Map<string, any>());
  const [stepTree, setStepTree] = useState<any | null>(null);
  const [size, setSize] = useState<Size>(Size['?']);
  const [sizingToolUIState, setSizingToolState] = useRecoilState(sizingToolState);
  const handle = sizingToolUIState?.product?.handle;
  const { setSize: updateSize } = useProductSelection(selectionType, selectionKey);
  const currentModalState = useRecoilValue(modalState);
  const [currentStepKey, goToStep] = useState<string>();

  const sizingToolWrapperRef = useRef<HTMLDivElement>(null);
  const [stepsMetadata, setStepsMetadata] = useState<StepViewModel[] | undefined>(handle && StepsMetadata[handle]);
  const [sessionGuid] = useState<string>(uuid());
  const [stepHistory, setStepHistory] = useState<string[]>(stepsMetadata ? [stepsMetadata[0].key] : []);
  const [userSelectedProductHandle, setUserSelectedProductHandle] = useState<ProductHandle>();
  const setModalState = useSetModalState();

  useEffect(() => {
    sizingToolWrapperRef.current?.scrollTo(0, 0);
  }, [currentStepKey]);

  const saveStartStep = useCallback(
    async (key: string, { value }: Pick<Option, 'value'>): Promise<void> => {
      const clientId = await GoogleAnalyticsService.instance.getClientId();

      if (!value) {
        return;
      }

      const response: SizingToolResponse = {
        funnelId,
        sessionGuid,
        clientId,
        engineValue: '?',
        value,
        stepKey: key,
        productHandle: handle,
      };
      return HoneyLoveService.instance.saveResponse(response);
    },
    [handle, sessionGuid],
  );

  const addStepToHistory = (stepKey: string) => {
    setStepHistory([...stepHistory, stepKey]);
  };

  const generateChildNodes = useCallback(
    (node: TreeNode, tree: any) => {
      const value = node.data().value;
      if (!value?.options?.length) {
        return;
      }
      const processedOptions: string[] = [];
      value.options.forEach((o) => {
        const nextStep = stepsMetadata?.find((s: StepViewModel) => s.key === o.nextStepKey);
        if (nextStep && processedOptions.indexOf(o.nextStepKey) === -1) {
          processedOptions.push(o.nextStepKey);
          const newNode = tree.insertToNode(node, {
            key: nextStep.key,
            value: nextStep,
          });
          if (o.nextStepKey) {
            generateChildNodes(newNode, tree);
          }
        }
      });
    },
    [stepsMetadata],
  );

  useEffect(() => {
    if (stepsMetadata) {
      const startStep = stepsMetadata[0];
      if (startStep) {
        const newStepTree = dataTree.create();
        const startNode: TreeNode = newStepTree.insert({
          key: startStep.key,
          value: startStep,
        });
        generateChildNodes(startNode, newStepTree);
        setStepTree(newStepTree);
      }
    }
  }, [stepsMetadata, generateChildNodes]);

  useEffect(() => {
    sizingToolUIState.sizingToolState === SizingToolState.Open && saveStartStep('[ACTION]', { value: 'START' });
  }, [sizingToolUIState.sizingToolState, saveStartStep]);

  const handleOptionSelected = async (step: StepViewModel, option: Option) => {
    saveResponse(step.key, option);
    if (option.nextStepKey === '') {
      // Save the response and short circuit
      return;
    }

    if (step.key === 'not-ready-s' && option?.value?.toUpperCase() === 'ORDER IN SIZE SMALL') {
      updateSize(LongSize['Small']);
    }

    if (step.key === 'not-ready' && option?.value?.toUpperCase() === 'ORDER IN SIZE 3X') {
      updateSize(LongSize['3X']);
    }

    stepValues.set(step.key, option);

    const calculatedSize = handle ? calculateSizeFromStepValues(stepValues, handle) : Size['?'];
    if (calculatedSize > Size['?']) {
      setSize(calculatedSize);
    }

    setStepValues(stepValues);
    const nextStep = stepsMetadata?.find((s: StepViewModel) => s.key === option.nextStepKey);

    if (nextStep && nextStep.bypass) {
      const bypassToStep = nextStep.bypass(stepValues);
      if (bypassToStep) {
        addStepToHistory(bypassToStep);
        goToStep(bypassToStep);
        return;
      }
    }
    addStepToHistory(option.nextStepKey);
    goToStep(option.nextStepKey);
  };

  // const goBack = () => {
  //   saveResponse('[ACTION]', { value: 'BACK' });
  //   reverse();
  // };

  const goBack = () => {
    saveResponse('[ACTION]', { value: 'BACK' });
    const backStepKey: string | undefined = stepHistory.length > 1 ? stepHistory[stepHistory.length - 2] : undefined;

    if (backStepKey) {
      setStepHistory(stepHistory.splice(0, stepHistory.length - 1));
      goToStep(backStepKey);
    } else if (currentStepKey?.toLowerCase() === stepsMetadata?.[0].key.toLowerCase()) {
      setSizingToolState({ ...sizingToolState, sizingToolState: SizingToolState.Open, product: undefined, selectionType, selectionKey });
      setStepTree(null);
      setStepHistory([]);
    }
  };

  const saveResponse = async (key: string, { value }: Pick<Option, 'value'>): Promise<void> => {
    const clientId = await GoogleAnalyticsService.instance.getClientId();

    if (!value) {
      return;
    }

    const response: SizingToolResponse = {
      funnelId,
      sessionGuid,
      clientId,
      engineValue: parseEngineVariables('[engine]', stepValues, handle, false),
      value: parseEngineVariables(value, stepValues, handle),
      stepKey: key,
      productHandle: handle,
    };

    return HoneyLoveService.instance.saveResponse(response);
  };

  // const currentStep = stepsMetadata?.find((s: StepViewModel) => s.key === currentModalState.sizingToolStepKey) || stepsMetadata?.[0];
  const currentStep = stepsMetadata?.find((s: StepViewModel) => s.key === currentStepKey) || (stepsMetadata && stepsMetadata[0]);
  const startStep = stepsMetadata?.[0];
  const hideBackBtn = sizingToolUIState.product === undefined || (currentStep?.key === startStep?.key && sizingToolUIState.initialProduct);

  const Title = () => (
    <div className={styles.sizingToolHeaderTitle}>
      {sizingToolUIState.product === undefined ? (
        <span>Select product</span>
      ) : (
        <div className={styles.hlStLogoWrapper}>
          <img className={styles.hlStLogo} src={assetPathFn('honeylove-brandmark-black.svg')} alt="honeylove logo" />
        </div>
      )}
    </div>
  );

  const selectProduct = (product: ViewProduct) => {
    if (handle !== product.handle) {
      setStepValues(new Map<string, any>());
    }
    setStepsMetadata(StepsMetadata[product.handle]);
    setSizingToolState({ sizingToolState: SizingToolState.Open, selectionType, selectionKey, product, skipTracking: true });
    const firstStep = StepsMetadata[product.handle]?.[0];
    goToStep(firstStep!.key);
    addStepToHistory(firstStep!.key);
    setUserSelectedProductHandle(product.handle);
  };

  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSizingToolState({ sizingToolState: SizingToolState.Closed, selectionType, selectionKey });
      }
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [setSizingToolState, selectionType, selectionKey]);

  const onSizeSelect = sizingToolUIState.onSizeSelect
    ? (selectedSize: LongSize, fit: Fit | undefined, confirmedForStepper: boolean) => {
        sizingToolUIState.onSizeSelect?.(selectedSize, fit, confirmedForStepper);
        setSizingToolState({ ...sizingToolUIState, sizingToolState: SizingToolState.Complete, selectionType, selectionKey });
        setModalState({ toggleState: ModalToggleState.Closed });
      }
    : undefined;

  return (
    <>
      <CardHeader />
      <CardBody style={{ height: 'inherit', maxHeight: 'inherit' }}>
        <div className={styles.hlStWrapper} ref={sizingToolWrapperRef}>
          <div className={styles.hlStContainer}>
            <div className={styles.sizingToolHeaderWrapper}>
              <DivButton
                className={styles.hlStBackButton}
                onClick={() => {
                  if (hideBackBtn) return;
                  goBack();
                }}
                label="Go back"
              >
                {!hideBackBtn && <Icon name={'chevron-left'} style={{ width: '14px' }} />}
              </DivButton>
              <Title />
              <DivButton
                className={styles.hlStCloseButton}
                onClick={() => {
                  saveResponse('[ACTION]', { value: 'CLOSE' });
                  if (currentModalState.onClose) {
                    currentModalState.onClose();
                  }
                }}
                label="Close sizing tool"
              >
                <Icon name="close" style={{ width: '16px' }} />
              </DivButton>
            </div>
            {stepTree && currentStep ? (
              <ProgressBar percentFill={getCurrentProgress(stepTree, currentStep)} style={{ height: '4px' }} />
            ) : (
              <ProgressBar percentFill={2} style={{ height: '4px' }} />
            )}
            {sizingToolUIState.product && currentStep && handle && (
              <Step
                selectionType={selectionType}
                selectionKey={selectionKey}
                size={size}
                data={currentStep}
                onOptionSelect={(option: Option) => {
                  handleOptionSelected(currentStep, option);
                }}
                activeMeasurement={activeMeasurement}
                selectedOption={stepValues.get(currentStep.key)}
                toggleMeasurementCallback={(newMeasurement: MeasurementSystem) => toggleMeasurement(newMeasurement)}
                stepValues={stepValues}
                productHandle={handle}
                subContext={subContext}
                onSizeSelect={onSizeSelect}
              />
            )}
            {(!sizingToolUIState.product || !handle) && <FindYourSizeSelector selectedHandle={userSelectedProductHandle} callback={selectProduct} />}
          </div>
        </div>
      </CardBody>
      <CardFooter />
    </>
  );
};

export default FunnelView;
